import {html, LitElement, type TemplateResult} from 'lit';
import {customElement, property, state} from 'lit/decorators.js';
import {classMap} from 'lit/directives/class-map.js';
import {createRef, ref} from 'lit/directives/ref.js';
import {join} from 'lit/directives/join.js';
import {Task} from '@lit/task';

import {API_PREFIX, APP_ELEMENT, ID_PREFIX} from "../../utils/constants";
import {data, onChanges, requestValidation} from "@@context";
import {apiFetch} from "../../utils/api";
import {debug} from "../../utils/log";
import {later} from "../../utils/basic";
import {translate} from "../../utils/i18n";
import {isErrorRetryable, translateError} from "../../utils/error";
import {closeOnBackdropClick} from "../../utils/dom";

import '../../assets/base.pcss'
import styles from './shadow.pcss?inline&shadow';
import shield from '../../assets/shield.svg?html';
import logo from '../../assets/logo.svg?html';
import retry from '../../assets/retry.svg?html';
import info from '../../assets/info.svg?html';
import close from '../../assets/close.svg?html';
import visibilityOff from '../../assets/visibility_off.svg?html';
import {LangAwareController} from "./lang-aware";

const isFallback = import.meta.env.MODE === "fallback"


const elementsWithAutoTheme = new Set<SpamShieldElement>();

function setAutoDark() {
    const root = document.documentElement;
    if (root.hasAttribute('data-theme')) {
        isAutoDark = (root.getAttribute('data-theme') || '') == 'dark';
    } else if (root.hasAttribute('data-bs-theme')) {
        isAutoDark = (root.getAttribute('data-bs-theme') || '') == 'dark';
    } else if (root.hasAttribute('theme')) {
        isAutoDark = (root.getAttribute('theme') || '') == 'dark';
    } else {
        isAutoDark = root.classList.contains('theme-dark') || root.classList.contains('dark');
    }
    elementsWithAutoTheme.forEach(el => applyAutoTheme(el));
}

(new MutationObserver(setAutoDark)).observe(document.documentElement, {
    attributes: true,
    childList: false,
    subtree: false
});
later(50).then(setAutoDark);

const isDarkQuery = matchMedia('(prefers-color-scheme: dark)');
let isAutoDark = false;

function applyAutoTheme(el: SpamShieldElement) {
    if (isAutoDark) {
        el.classList.remove('_⛨-light');
        el.classList.add('_⛨-dark');
    } else {
        el.classList.remove('_⛨-dark');
        el.classList.add('_⛨-light');
    }
}

type ClassifyClientResponse = {
    classification: string;
    baselineYear: number;
    country: string;
    device: string;
    isBot: boolean;
    isMobile: boolean;
    isTablet: boolean;
    isDesktop: boolean;
    isModernBrowser: boolean;
    releaseDate: string;
    os: { name: string; version: string };
    browser: { name: string; version: string };
}

export function injectAutoElement() {
    const existing = document.querySelector(APP_ELEMENT);
    if (existing) {
        return existing;
    }
    const el = document.createElement(APP_ELEMENT);
    document.body.appendChild(el);
    return el;
}

/**
 * A custom element implementing the SpamShield mechanism, providing visual states and functionalities to mitigate spam-related interactions.
 * Extends LitElement to leverage reactive properties and templating for dynamic updates.
 *
 * Properties:
 * - `ui` ('never' | 'static' | 'fixed'): Specifies the UI mode.
 * - `theme` ('auto' | 'light' | 'dark'): Controls the theme of the element.
 * - `lang` (string): Represents the language of the element.
 * - `state` (readonly): Reflects the current state of the internal `State` instance.
 *
 * Methods:
 * - `classifyClient()`: Asynchronously classifies the client based on various parameters like device, browser, OS, etc.
 *
 * Lifecycle Callbacks:
 * - `connectedCallback()`: Activates the element and integrates it into the DOM, resuming its internal state and observing document-level attributes.
 * - `disconnectedCallback()`: Cleans up and suspends the element's state when removed from the DOM.
 * - `updated(changedProperties)`: Reacts to property changes; in particular, updates CSS classes when the `theme` changes.
 *
 * Protected Methods:
 * - `render()`: Defines the component's UI structure using a reactive rendering mechanism.
 *
 * Internal Mechanisms:
 * - Uses a reactive `State` instance for managing state transitions and changes.
 * - Maintains a `Task` for managing visibility logic based on UI mode and state changes.
 */
@customElement(APP_ELEMENT)
export class SpamShieldElement extends LitElement {
    static styles = [styles];

    @property({reflect: true})
    ui: 'never' | 'static' | 'fixed' = 'fixed'

    @property({reflect: true})
    theme: 'auto' | 'system' | 'light' | 'dark' = 'light'

    @property({})
    lang: string = '';

    @property({reflect: true})
    get state() {
        return data.state;
    }

    get isFallback() {
        return isFallback;
    }

    @state()
    private _hide = false;
    @state()
    private _multiline = false;

    #visibilityId = 0
    #visibilityController = new Task(this, {
        args: () => [this.ui, this.state] as const,
        task: async ([ui, state], {signal}): Promise<boolean> => {
            clearTimeout(this.#visibilityId)
            if (this._hide) {
                this._hide = false;
            }
            this.#visibilityId = 0
            debug("Visibility task started", {ui, state});
            if (ui === 'never') {
                return false
            }
            if (ui === 'static') {
                return true;
            }
            if (state === 'error') {
                return !(typeof sessionStorage !== 'undefined' && sessionStorage.getItem(ID_PREFIX + 'hide') === 'true');
            }
            await later(30_000, {signal});
            if (data.state === 'solved') {
                const previousVisibility = this.#visibilityController.value;
                debug("state is solved", {previousVisibility})
                if (previousVisibility) {
                    this.#visibilityId = window.setTimeout(() => {
                        if (data.state === 'solved') {
                            this._hide = true;
                        }
                    }, 2000);
                } else {
                    debug("state became solved without me being visible", {previousVisibility})
                }
                return previousVisibility || false
            }
            return true;
        }
    })

    #aboutDialog = createRef<HTMLDialogElement>();
    #innerSpan = createRef<HTMLSpanElement>();

    // @ts-ignore
    #langController = new LangAwareController(this)

    get token(): string | undefined {
        if (data.solution) {
            return `v=1&lang=${this.#langController.lang}|${data.solution}`;
        }
        return ''
    }

    async classifyClient({signal}: { signal?: AbortSignal } = {}): Promise<ClassifyClientResponse> {
        return await apiFetch<ClassifyClientResponse>(API_PREFIX + 'request/classify', {signal})
    }

    #classifyController = new Task(this, {
        autoRun: false,
        args: () => [] as const,
        task: async ([], {signal}) => {
            return await this.classifyClient({signal});
        }
    })

    #unlisten: (() => void) | undefined;

    override connectedCallback() {
        super.connectedCallback();
        this.#unlisten = onChanges(['state', 'solution', 'rate'], (changes) => {
            for (const change of changes) {
                if (change.key === 'state') {
                    this.requestUpdate('state', change.oldValue);
                    debug("State changed:", change.value);
                } else if (change.key === 'solution') {
                    debug("Solution changed:", change.value);
                    this.requestUpdate('solution', change.oldValue);
                } else if (change.key === 'rate') {
                    debug("Rate changed:", change.value);
                    this.requestUpdate();
                }
            }
        })
        if (this.theme === 'auto') {
            Promise.resolve().then(() => applyAutoTheme(this));
            elementsWithAutoTheme.add(this);
        }
    }

    override disconnectedCallback() {
        if (this.#unlisten) {
            this.#unlisten();
            this.#unlisten = undefined;
        }
        elementsWithAutoTheme.delete(this)
        super.disconnectedCallback();
    }

    #updateSystemTheme() {
        if (isDarkQuery.matches) {
            this.classList.remove('_⛨-light');
            this.classList.add('_⛨-dark');
        } else {
            this.classList.remove('_⛨-dark');
            this.classList.add('_⛨-light');
        }
    }

    #listeningForSystemTheme = false;

    override updated(changedProperties: Map<string, unknown>) {
        super.updated(changedProperties);
        if (changedProperties.has('theme')) {
            switch (this.theme) {
                case 'light':
                    this.classList.remove('_⛨-dark');
                    this.classList.add('_⛨-light');
                    elementsWithAutoTheme.delete(this)
                    break;
                case 'dark':
                    this.classList.remove('_⛨-light');
                    this.classList.add('_⛨-dark');
                    elementsWithAutoTheme.delete(this)
                    break;
                case 'auto':
                    applyAutoTheme(this)
                    elementsWithAutoTheme.add(this)
                    // Apply the auto theme after a short delay to ensure the DOM is ready (allows the initial theme to be applied)
                    later(1000).then(() => {
                        if (this.theme == 'auto') {
                            applyAutoTheme(this)
                        }
                    });
                    break;
                case 'system':
                    this.#updateSystemTheme();
                    elementsWithAutoTheme.delete(this)
                    break;
                default:
                    this.classList.remove('_⛨-light', '_⛨-dark');
            }
            if (this.theme === 'system' && !this.#listeningForSystemTheme) {
                isDarkQuery.addEventListener('change', this.#updateSystemTheme);
                this.#listeningForSystemTheme = true;
            } else if (this.theme !== 'system' && this.#listeningForSystemTheme) {
                isDarkQuery.removeEventListener('change', this.#updateSystemTheme);
                this.#listeningForSystemTheme = false;
            }
        }
        this.#innerSpanMultiline()
    }

    protected override render() {
        const {ui, theme, state} = this;
        const visible = (this.#visibilityController.value || false) && !this._hide;
        let text = '…';
        const lang = this.#langController.lang;
        switch (state) {
            case 'initializing':
                text = translate(lang, 'status.initializing');
                break;
            case 'validating':
                text = translate(lang, 'status.validating');
                break;
            case 'solving':
                text = translate(lang, 'status.solving');
                if (data.rate !== undefined && data.rate > 0) {
                    text += ` (${translate(lang, 'status.rate', {rate: data.rate.toFixed(1).replace(/\.0$/, '')})})`;
                }
                break;
            case 'solved':
                text = translate(lang, 'status.solved');
                break;
            case 'error':
                text = translateError(lang, data.errorId, data.errorMsg) + "\n" + translate(lang, 'app.error.suffix');
                break;
        }
        const actions: TemplateResult[] = []
        if (!isFallback && state === 'error' && isErrorRetryable(data.errorId)) {
            actions.push(html`
                <button title="${translate(lang, 'app.retry')}" @click="${() => requestValidation()}">${retry}
                </button>`);
        }
        if (actions.length == 0 && state === 'error' && ui == 'fixed' && typeof sessionStorage !== 'undefined') {
            actions.push(html`
                <button title="${translate(lang, 'app.hide')}" @click="${() => {
                    sessionStorage.setItem(ID_PREFIX + 'hide', 'true');
                    this.#visibilityController.run([ui, state]);
                }}">${visibilityOff}
                </button>`);
        }
        if (visible) {
            actions.push(html`
                <button title="${translate(lang, 'app.about.button')}" @click="${() => this.#showAboutDialog()}">${info}
                </button>`);
        }
        const hasActions = actions.length > 0;
        return html`
            <div class="${classMap({
                wrapper: true,
                [`ui-${ui}`]: true,
                [`theme-${theme}`]: true,
                [`state-${state}`]: true,
                visible,
                hasActions,
                multiline: this._multiline,
            })}">
                <div class="logo">${shield}</div>
                <div class="text"><span .innerText="${text}" ${ref(this.#innerSpan)}></span></div>
                ${hasActions ? html`
                    <div class="actions">${join(actions, () => {
                    })}
                    </div>` : ''}
            </div>
            <dialog ${ref(this.#aboutDialog)} @click="${closeOnBackdropClick}" aria-labelledby="about-title">
                <form method="dialog" class="dialog-content">
                    <div class="dialog-logo">${logo}</div>
                    <h1 class="dialog-title" id="about-title">${translate(lang, 'app.about.title')}</h1>
                    <button class="dialog-close" type="submit" title="${translate(lang, 'app.close')}">${close}</button>
                    <div class="dialog-body">
                        <div .innerText="${translate(lang, 'app.about.body')}"></div>
                        <div class="classification">
                            ${this.#classifyController.render({
                                initial: () => html`
                                    <button class="btn" type="button" @click="${() => this.#classifyController.run()}">
                                        ${translate(lang, 'app.classification.show')}
                                    </button>`,
                                pending: () => html`
                                    <button class="btn" disabled>${translate(lang, 'loading')}</button>`,
                                error: (error) => html`
                                    <div class="error">${error}</div>`,
                                complete: (result) => this.#renderClassification(result),
                            })}
                        </div>
                    </div>
                </form>
            </dialog>
        `;
    }

    #showAboutDialog() {
        if (this.#aboutDialog.value && typeof this.#aboutDialog.value.showModal === 'function') {
            this.#aboutDialog.value.showModal();
        } else {
            const lang = this.#langController.lang;
            alert(`${translate(lang, 'app.about.title')}\n\n${translate(lang, 'app.about.body')}`);
        }
    }

    async #innerSpanMultiline() {
        await Promise.resolve();
        const rects = this.#innerSpan.value ? this.#innerSpan.value.getClientRects() : [];
        if (rects.length === 0) {
            if (this._multiline) {
                this._multiline = false;
            }
            return;
        }
        let y = rects[0].y;
        for (let i = 1; i < rects.length; i++) {
            if (rects[i].y !== y) {
                if (!this._multiline) {
                    this._multiline = true;
                }
                return;
            }
        }
        if (this._multiline) {
            this._multiline = false;
        }
    }

    #renderClassification(result: ClassifyClientResponse) {
        const lang = this.#langController.lang;
        const browserDetails = []
        if (result.baselineYear) {
            browserDetails.push(translate(lang, 'app.classification.baseline', {year: result.baselineYear}));
        }
        if (result.releaseDate && result.releaseDate != 'unknown') {
            let date = result.releaseDate;
            try {
                date = new Intl.DateTimeFormat(lang).format(new Date(result.releaseDate));
            } catch {
            }
            browserDetails.push(translate(lang, 'app.classification.releasedAt', {date: date}));
        }
        return html`
            <dl>
                <dt>${translate(lang, 'app.classification.country')}</dt>
                <dd>${result.country}</dd>
                <dt>${translate(lang, 'app.classification.os')}</dt>
                <dd>${result.os && result.os.name ? `${result.os.name} ${result.os.version}` : 'unknown'}</dd>
                <dt>${translate(lang, 'app.classification.browser')}</dt>
                <dd>
                    ${result.browser && result.browser.name ? `${result.browser.name} ${result.browser.version}` : 'unknown'}
                    ${browserDetails.length ? ` (${browserDetails.join(', ')})` : ''}
                </dd>
                <dt>${translate(lang, 'app.classification.flags')}</dt>
                <dd>
                    ${[
                        result.isBot ? translate(lang, 'app.classification.flag.bot') : '',
                        result.isMobile ? translate(lang, 'app.classification.flag.mobile') : '',
                        result.isTablet ? translate(lang, 'app.classification.flag.tablet') : '',
                        result.isDesktop ? translate(lang, 'app.classification.flag.desktop') : '',
                        result.isModernBrowser ? translate(lang, 'app.classification.flag.modern') : ''
                    ].filter(e => !!e).join(', ') || 'none'}
                </dd>
                <dt>${translate(lang, 'app.classification.classification')}</dt>
                <dd>${result.classification}</dd>
            </dl>`
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [APP_ELEMENT]: SpamShieldElement;
    }
}
